import React, { useState, useReducer, useEffect } from "react";
import { useDispatch } from "react-redux";

// Components
import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import InputField from "../Components/Forms/InputField";
import SelectField from "../Components/Forms/SelectField";
import SearchInputField from "../Components/Forms/SearchInputField";
import ModalSearchEquipment from "./ModalSearchEquipment";
import ModalSearchChip from "./ModalSearchChip";
import ImportModal from "../Components/Modals/Import-modal";

// Services
import { equipmentService } from "../../Services/equipment.service";
import { equipmentModelsService } from "../../Services/equipment.models.service";
import { accreditedService } from "../../Services/accredited.service";
import { chipService } from "../../Services/chip.service";
import { toDateTimeISOString } from "../../Services/string.service";
import { importService } from "../../Services/import.service";
import { fileService } from "../../Services/file.service";

// Icons
import { AiOutlineSearch, AiFillSave } from "react-icons/ai";
import { FcSmartphoneTablet } from "react-icons/fc";
import { CgImport } from "react-icons/cg";

// Constants
const _pageTitle = "Cadastro de Equipamentos";
const _registerTitle = "Cadastrando novo Equipamento";
const _editTitle = "Editando Equipamento";

const formInitialValues = {
	id: 0,
	serial: 0,
	modelEquipmentId: "",
	lineNumber: "",
	iccid: "",
	mobileOperatorId: 0,
	accreditedId: 0,
	address: "",
	statusEquipmentId: 0,
	privateAPNSince: "",
	ChipId: 0,
};

const formReducer = (state, event) => {
	if (event.setter) {
		return event.equipment;
	}
	if (event.reset) {
		return formInitialValues;
	}

	return {
		...state,
		[event.name]: event.value,
	};
};

const filterReducer = (state, event) => {
	if (event.reset) {
		return { equipmentModelId: "", equipmentStatusId: "", serial: "", address: "" };
	}
	return {
		...state,
		[event.name]: event.value,
	};
};

const filterChipReducer = (state, event) => {
	if (event.reset) {
		return { lineNumber: "", iccid: "" };
	}
	return {
		...state,
		[event.name]: event.value,
	};
};

const Equipments = () => {
	const [formData, setFormData] = useReducer(formReducer, {});
	const [isLoading, setIsLoading] = useState(false);
	const [toastParams, setToastParams] = useState("");
	const [actionTitle, setActionTitle] = useState("Dado do Equipamento");
	const [showSearchModal, setShowSearchModal] = useState(false);
	const [showChipModal, setShowChipModal] = useState(false);
	const [showFields, setShowFields] = useState(false);
	const [showDelete, setShowDelete] = useState(false); // eslint-disable-line no-unused-vars
	const [filter, setFilter] = useReducer(filterReducer, {}); // eslint-disable-next-line
	const [chipFilter, setChipFilter] = useReducer(filterChipReducer, {});
	const [selectedEquipment, setSelectedEquipment] = useState({}); // eslint-disable-line no-unused-vars
	const [selectedChip, setSelectedChip] = useState({}); // eslint-disable-next-line
	const [listModels, setListModels] = useState([]);
	const [listAccredited, setListAccredited] = useState([]);
	const [listStatus, setListStatus] = useState([]);
	const [listOperators, setListOperators] = useState([]);
	const [showImportModal, setShowImportModal] = useState(false);
	const [readOnlyFields, setReadOnlyFields] = useState({});

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch({ type: "title", value: _pageTitle });
	}, [dispatch]);

	const onSearch = () => {
		if (filter.equipmentModelId || filter.equipmentStatusId || filter.serial || filter.address) {
			setShowSearchModal(true);
		}
	};

	const onSearchChip = () => {
		if (chipFilter.lineNumber) {
			setShowChipModal(true);
		}
	};

	const onNew = () => {
		setShowDelete(false);
		setFormData({
			reset: true,
		});
		setReadOnlyFields({});
		setShowFields(true);
		setActionTitle(_registerTitle);
	};

	const onCancel = () => {
		setFormData({
			reset: true,
		});
		setShowFields(false);
		setShowDelete(false);
		setSelectedChip({});
		setChipFilter({
			reset: true,
		});
		setReadOnlyFields({});
	};

	const loadReadOnlyFields = async (equipment) => {
		const details = await equipmentService.fetchEquipment({
			equipmentId: equipment?.id ?? equipment?.equipmentId,
			serialNumber: equipment?.serial,
		});
		setReadOnlyFields(details || {});
	};

	const handleFilterChange = (event) => {
		setFilter({
			name: event.target.name,
			value: event.target.value.trim(),
		});
	};

	const handleFilterChipChange = (event) => {
		setChipFilter({
			name: event.target.name,
			value: event.target.value.trim(),
		});
	};

	const handleFormChange = async (event) => {
		setFormData({
			name: event.target.name,
			value: event.target.value,
		});
	};

	const handleSelectEquipment = async (equipment) => {
		setSelectedEquipment(equipment);

		// Normaliza os campos da nova API (/Equipment/api/v3/equipment) pros
		// nomes internos que o formulário já usa (serial, modelEquipmentId, etc).
		const normalized = {
			id: equipment.equipmentId ?? equipment.EquipmentId ?? equipment.id,
			serial: equipment.serialNumber ?? equipment.SerialNumber ?? equipment.serial,
			modelEquipmentId:
				equipment.equipmentModelId ?? equipment.EquipmentModelId ?? equipment.modelEquipmentId,
			address: equipment.address ?? equipment.Address,
			statusEquipmentId:
				equipment.equipmentStatusId ?? equipment.EquipmentStatusId ?? equipment.statusEquipmentId,
			accreditedId: equipment.accreditedId ?? equipment.AccreditedId,
			lineNumber: equipment.lineNumber ?? "",
		};

		setFormData({
			setter: true,
			equipment: normalized,
		});

		if (normalized.lineNumber) {
			var chip = await chipService.searchByLineNumber(normalized.lineNumber);
			if (chip && chip.length > 0) {
				setSelectedChip(chip[0]);
				setChipFilter({
					name: "lineNumber",
					value: chip[0].numberLine,
				});
			}
		}

		loadReadOnlyFields(normalized);

		setShowFields(true);
		setShowSearchModal(false);
		setActionTitle(_editTitle);
		setShowDelete(true);
	};

	const handleSelectChip = async (chip) => {
		setSelectedChip(chip);
		setChipFilter({
			name: "lineNumber",
			value: chip.numberLine,
		});
		setShowChipModal(false);
	};

	const onSave = async () => {
		if (selectedChip?.id == null) {
			setToastParams(
				JSON.stringify({
					show: true,
					title: "Atenção",
					body: "Pesquise e selecione uma linha!",
					color: "Warning",
				})
			);
			return;
		}
		if (!formData.serial) {
			setToastParams(
				JSON.stringify({
					show: true,
					title: "Atenção",
					body: "Forneça um serial!",
					color: "Warning",
				})
			);
			return;
		}
		if (!formData.modelEquipmentId) {
			setToastParams(
				JSON.stringify({
					show: true,
					title: "Atenção",
					body: "Forneça um modelo!",
					color: "Warning",
				})
			);
			return;
		}
		if (!formData.statusEquipmentId) {
			setToastParams(
				JSON.stringify({
					show: true,
					title: "Atenção",
					body: "Forneça o Status!",
					color: "Warning",
				})
			);
			return;
		}

		// Define demais campos
		formData.startDate = new Date().toISOString()
		formData.chipId = selectedChip?.id;

		try {
			delete formData["iccId"];
			delete formData["modelEquipmentName"];
			delete formData["statusEquipmentName"];
			delete formData["line"];
		} catch (error) {}

		try {
			setIsLoading(true);

			var created = await equipmentService.save(formData);
			setFormData({
				name: "id",
				value: created,
			});
			if (created) {
				setToastParams(
					JSON.stringify({
						show: true,
						title: "Sucesso",
						body: "Equipamento salvo com sucesso!",
						color: "Success",
					})
				);
				loadReadOnlyFields({ equipmentId: created, serial: formData.serial });
			}
		} catch (error) {
			console.log(error);
			setToastParams(
				JSON.stringify({
					show: true,
					title: "Erro",
					body: "Não foi possível salvar. Tente novamente mais tarde.",
					color: "Danger",
				})
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCloseImport = () => {
		setShowImportModal(false);
	};

	const onImport = () => {
		setShowImportModal(true);
	};

	const handleImport = async (file) => {
		let base64File = await fileService.toBase64(file);
		base64File = base64File.split("base64,")[1];

		var payload = {
			scheduleTypeId: "Equipamentos",
			fileName: file.name,
			fileType: file.type,
			contentType: file.type,
			contentFile: base64File,
			usePeriod: false,
			dateTimeStart: toDateTimeISOString(new Date()),
			dateTimeEnd: toDateTimeISOString(new Date()),
			created: toDateTimeISOString(new Date()),
		};

		await importService.upload(payload);

		setShowImportModal(false);

		setToastParams(
			JSON.stringify({
				show: true,
				title: "Sucesso",
				body: "Importação do arquivo realizada!",
				color: "Success",
			})
		);
	};

	useEffect(() => {
		(async () => {
			// Modelo agora vem da mesma fonte usada em /equipment-models.
			var response_models = await equipmentModelsService.fetchEquipmentModels();
			if (Array.isArray(response_models)) {
				const withKeys = response_models.map((item) => ({
					id: item.equipmentModelId ?? item.EquipmentModelId,
					name: item.description ?? item.Description ?? item.equipmentModelName ?? item.EquipmentModelName,
					key: crypto.randomUUID(),
				}));
				setListModels(withKeys);
			}

			// Credenciado agora vem do serviço dedicado (Accredited/api/v3/accredited).
			var response_acrredited = await accreditedService.fetchAccredited();
			if (Array.isArray(response_acrredited)) {
				const withKeys = response_acrredited.map((item) => ({
					id: item.accredited ?? item.Accredited ?? item.accreditedId ?? item.AccreditedId,
					name: item.name ?? item.Name ?? "",
					key: crypto.randomUUID(),
				}));
				setListAccredited(withKeys);
			}

			// Status agora vem do endpoint dedicado /equipment/status.
			var response_status = await equipmentService.fetchEquipmentStatus();
			if (Array.isArray(response_status)) {
				const withKeys = response_status.map((item) => ({
					id: item.equipmentStatusId ?? item.EquipmentStatusId,
					name: item.description ?? item.Description,
					key: crypto.randomUUID(),
				}));
				setListStatus(withKeys);
			}

			var response_operators = await chipService.listMobileOperator();
			if (response_operators) {
				const withKeys = response_operators.map((item) => {
					return {
						...item,
						key: crypto.randomUUID(),
					};
				});
				setListOperators(withKeys);
			}
		})();
	}, []);

	return (
		<>
			<div className="container-xxl flex-grow-1 container-p-y">
				{isLoading ? <Spinner /> : ""}

				<ToastComponent
					params={toastParams}
					closeToast={() => setToastParams(JSON.stringify({ show: false }))}
				/>

				<ModalSearchEquipment
					show={showSearchModal}
					handleClose={setShowSearchModal}
					filter={filter}
					handleSelect={handleSelectEquipment}
					listModels={listModels}
					listStatus={listStatus}
				/>

				<ModalSearchChip
					show={showChipModal}
					handleClose={setShowChipModal}
					filter={chipFilter}
					handleSelect={handleSelectChip}
				/>

				<ImportModal
					title={"Equipamentos"}
					callBackImport={handleImport}
					show={showImportModal}
					handleClose={handleCloseImport}
					disabled={true}
				/>

				{/* <DeleteModal
          itemName={isDeleting.itemName}
          itemRemoveId={isDeleting.itemRemoveId}
          callBackRemove={handleRemove}
          show={isDeleting.show}
          handleClose={handleCloseModal}
        /> */}

				<div className="row">
					<h5>Preencha os campos para buscar ou crie um novo</h5>
					<div className="row">
						<div className="mb-3 col-md-2">
							<div className="row">
								<SelectField
									name="equipmentModelId"
									label="Modelo"
									grid_length="8"
									itens={listModels}
									value={filter.equipmentModelId || ""}
									onChange={handleFilterChange}
								/>
							</div>
						</div>
						<div className="mb-3 col-md-2">
							<div className="row">
								<SelectField
									name="equipmentStatusId"
									label="Status"
									grid_length="8"
									itens={listStatus}
									value={filter.equipmentStatusId || ""}
									onChange={handleFilterChange}
								/>
							</div>
						</div>
						<div className="mb-3 col-md-2">
							<div className="mb-3">
								<SearchInputField
									name="serial"
									label="Serial"
									type="text"
									tabIndex="1"
									onChange={handleFilterChange}
								/>
							</div>
						</div>
						<div className="mb-3 col-md-2">
							<div className="mb-3">
								<SearchInputField
									name="address"
									label="Endereço"
									type="text"
									tabIndex="2"
									onChange={handleFilterChange}
								/>
							</div>
						</div>

						<div className="mb-3 col-md-4 col-lg-4">
							<div className="d-flex justify-content-around">
								<div>
									<button
										type="button"
										className="btn btn-primary"
										tabIndex={3}
										disabled={showFields}
										onClick={onSearch}
									>
										<AiOutlineSearch className="icon-in-button" />
										Buscar
									</button>
								</div>
								<div>
									<button
										type="button"
										className="btn btn-primary"
										disabled={showFields}
										onClick={onNew}
										tabIndex={4}
									>
										<FcSmartphoneTablet className="icon-in-button" />
										&nbsp; Novo
									</button>
								</div>
								<div>
									<button
										type="button"
										className="btn btn-primary"
										onClick={onImport}
										tabIndex={5}
									>
										<CgImport className="icon-in-button" />
										&nbsp; Importar
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					className="row"
					style={showFields ? { display: "" } : { display: "none" }}
				>
					<h4 className="fw-bold py-3 mb-4">
						<span className="text-muted fw-light">{actionTitle} </span>
					</h4>

					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="Serial"
								name="serial"
								type="text"
								tabIndex="5"
								grid_length="6"
								value={formData.serial || ""}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="modelEquipmentId"
								label="Modelo"
								tabIndex="6"
								grid_length="6"
								itens={listModels}
								value={formData.modelEquipmentId || ""}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="accreditedId"
								label="Credenciado"
								tabIndex="7"
								grid_length="7"
								itens={listAccredited}
								value={formData.accreditedId || ""}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<InputField
								name="address"
								label="Endereço"
								type="text"
								tabIndex="8"
								grid_length="8"
								value={formData.address || ""}
								onChange={handleFormChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="statusEquipmentId"
								label="Status"
								tabIndex="9"
								grid_length="6"
								itens={listStatus}
								value={formData.statusEquipmentId || ""}
								onChange={handleFormChange}
							/>
						</div>
					</div>
					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="Número da linha"
								name="lineNumber"
								type="number"
								tabIndex="10"
								grid_length="6"
								value={chipFilter?.lineNumber || ""}
								onChange={handleFilterChipChange}
							/>
						</div>

						<div className="row mb-3">
							<SelectField
								name="mobileOperatorId"
								label="Operadora"
								tabIndex="7"
								grid_length="8"
								readOnly={true}
								value={selectedChip?.mobileOperatorId || ""}
								itens={listOperators}
							/>
						</div>

						<div className="row mb-3">
							<InputField
								label="ICCID"
								name="iccid"
								type="number"
								tabIndex="6"
								grid_length="6"
								readOnly={true}
								value={selectedChip?.iccid || ""}
							/>
						</div>
					</div>

					<div className="col-4">
						<button
							type="button"
							className="btn btn-primary"
							tabIndex={3}
							onClick={onSearchChip}
						>
							<AiOutlineSearch className="icon-in-button" />
							Buscar
						</button>
					</div>

					{/* Campos somente-leitura vindos do GET — não fazem parte do
					    POST/PUT (ver equipmentService.fetchEquipment). Por enquanto só
					    exibição; a edição desses dados fica pra uma próxima etapa. */}
					<div className="col-12">
						<h6 className="text-muted mt-2 mb-3">Dados do Equipamento (somente leitura)</h6>
					</div>

					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="Versão de Hardware"
								name="hardwareVersion"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.hardwareVersion ?? readOnlyFields.HardwareVersion ?? ""}
							/>
						</div>
						<div className="row mb-3">
							<InputField
								label="ICCID do Equipamento"
								name="equipmentIccid"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.equipmentIccid ?? readOnlyFields.EquipmentIccid ?? ""}
							/>
						</div>
						<div className="row mb-3">
							<InputField
								label="APN"
								name="apn"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.apn ?? readOnlyFields.Apn ?? ""}
							/>
						</div>
					</div>

					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="Versão de Software"
								name="softwareVersion"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.softwareVersion ?? readOnlyFields.SoftwareVersion ?? ""}
							/>
						</div>
						<div className="row mb-3">
							<InputField
								label="ICCID do Chip Vinculado"
								name="registeredIccid"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.registeredIccid ?? readOnlyFields.RegisteredIccid ?? ""}
							/>
						</div>
						<div className="row mb-3">
							<InputField
								label="IP"
								name="ip"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.ip ?? readOnlyFields.Ip ?? ""}
							/>
						</div>
					</div>

					<div className="col-4">
						<div className="row mb-3">
							<InputField
								label="IMEI"
								name="imei"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.imei ?? readOnlyFields.Imei ?? ""}
							/>
						</div>
						<div className="row mb-3">
							<InputField
								label="ICCID Legado"
								name="legacyIccidDate"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.legacyIccidDate ?? readOnlyFields.LegacyIccidDate ?? ""}
							/>
						</div>
						<div className="row mb-3">
							<InputField
								label="Data do IP"
								name="ipDate"
								type="text"
								grid_length="6"
								readOnly={true}
								value={readOnlyFields.ipDate ?? readOnlyFields.IpDate ?? ""}
							/>
						</div>
					</div>

					<div className="mt-4">
						<button
							type="button"
							tabIndex={9}
							className="btn btn-primary me-2"
							onClick={async () => onSave()}
						>
							<AiFillSave className="icon-in-button" />
							&nbsp; Salvar
						</button>

						<button
							type="reset"
							className="btn btn-outline-secondary  me-2"
							onClick={onCancel}
							tabIndex={10}
						>
							Cancelar
						</button>

						{/* <button
              type='button'
              className='btn btn-danger'
              tabIndex={11}
              style={showDelete ? { display: "" } : { display: "none" }}>
              Remover
            </button> */}
					</div>
				</div>
			</div>
		</>
	);
};
export default Equipments;
